import {
  Timestamp,
  GeoPoint,
  DocumentReference,
  type FirestoreDataConverter,
  type DocumentData,
  type WithFieldValue,
  type SetOptions,
} from 'firebase/firestore'

export function convertFromFirestore(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof GeoPoint) return { lat: value.latitude, lng: value.longitude }
  if (value instanceof DocumentReference) return value
  if (Array.isArray(value)) return value.map(convertFromFirestore)
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = convertFromFirestore(v)
    }
    return result
  }
  return value
}

export function convertToFirestore(data: unknown): DocumentData | null {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return null
  }

  // Handle primitive types
  if (typeof data !== 'object') {
    return data as DocumentData
  }

  // Handle Date objects
  if (data instanceof Date) {
    return Timestamp.fromDate(data)
  }

  // Handle DocumentReference
  if (data instanceof DocumentReference) {
    return data
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertToFirestore(item)) as DocumentData
  }

  // Handle objects
  const result: Record<string, DocumentData | null> = {}
  for (const [key, value] of Object.entries(data)) {
    // Skip Vue's internal properties
    if (key.startsWith('_') || key.startsWith('$')) {
      continue
    }

    // Skip functions
    if (typeof value === 'function') {
      continue
    }

    // Convert the value, including undefined values
    const convertedValue = convertToFirestore(value)
    if (convertedValue !== null) {
      result[key] = convertedValue
    }
  }
  return result
}

export function globalConverter<T>(): FirestoreDataConverter<T & { id?: string }, DocumentData> {
  return {
    toFirestore(model: WithFieldValue<T & { id?: string }>, _options?: SetOptions): DocumentData {
      const { id: _id, ...rest } = model
      return convertToFirestore(rest) as DocumentData
    },
    fromFirestore(snapshot, options): T & { id: string } {
      const data = snapshot.data(options)
      return {
        ...(convertFromFirestore(data) as T),
        id: snapshot.id,
      }
    },
  }
}
